import useMediaQuery from './useMediaQuery';

/**
 * Responsive breakpoints, kept in sync with the Tailwind `screens` config in
 * `platform/ui/tailwind.config.js` (sm 640, md 768, lg 1024, xl 1280) so that
 * JS-driven layout decisions and `sm:`/`md:`/`lg:` utility classes agree.
 */
export const BREAKPOINTS = {
  sm: 640,
  md: 768,
  lg: 1024,
  xl: 1280,
} as const;

export interface ScreenSize {
  /** width < 768px — phones and small tablets in portrait */
  isMobile: boolean;
  /** 768px ≤ width < 1024px — tablets */
  isTablet: boolean;
  /** width ≥ 1024px — desktops and large tablets in landscape */
  isDesktop: boolean;
  /** width < 1024px — convenience flag for "use the compact / drawer layout" */
  isCompact: boolean;
  /** the device's primary pointer is coarse (touch) rather than a mouse */
  isTouch: boolean;
}

/**
 * Reports the current responsive bucket based on the shared OHIF breakpoints.
 * The consuming component re-renders whenever the viewport crosses a breakpoint
 * or the pointer type changes.
 *
 * Use `isCompact` to switch the viewer between the desktop (resizable columns)
 * and compact (overlay drawer) layouts.
 */
export default function useScreenSize(): ScreenSize {
  const isMobile = useMediaQuery(`(max-width: ${BREAKPOINTS.md - 1}px)`);
  const isTablet = useMediaQuery(
    `(min-width: ${BREAKPOINTS.md}px) and (max-width: ${BREAKPOINTS.lg - 1}px)`
  );
  const isDesktop = useMediaQuery(`(min-width: ${BREAKPOINTS.lg}px)`);
  const isTouch = useMediaQuery('(pointer: coarse)');

  return {
    isMobile,
    isTablet,
    isDesktop,
    isCompact: !isDesktop,
    isTouch,
  };
}
